import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import ComicCard from '@/components/feature/ComicCard';
import { useSEO } from '@/hooks/useSEO';
import { getCategoryByKey, queryComics } from '@/utils/comic';
import CategoryFilter from './components/CategoryFilter';

const PAGE_SIZE = 10;

export default function CategoryPage() {
  const { categoryName = 'all' } = useParams();
  const [searchParams] = useSearchParams();
  const category = getCategoryByKey(categoryName);
  const isAll = categoryName === 'all' || !category;

  const displayName = isAll ? '全部漫画' : (category?.name ?? categoryName);
  const description = isAll
    ? '汇聚全站精选韩漫作品，支持地区、题材、状态多维度筛选，轻松找到你想看的那一部。'
    : category?.description ?? '该分类下暂未收录内容。';

  useSEO(
    `${displayName}-韩漫漫画精选在线阅读`,
    isAll
      ? '韩漫网址全部漫画分类，涵盖恋爱、奇幻、校园、悬疑等题材的优质韩漫作品，支持多维度筛选与排序。'
      : `${displayName}漫画精选合集：${description} 韩漫网址提供在线阅读与章节更新追踪。`,
    `${displayName},韩漫,${displayName}漫画,韩漫在线阅读,漫画分类`,
  );

  const [region, setRegion] = useState('全部');
  const [status, setStatus] = useState('全部');
  const [tag, setTag] = useState('全部');
  const [sort, setSort] = useState(searchParams.get('sort') || 'updated');
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [categoryName, region, status, tag, sort]);

  const result = useMemo(
    () =>
      queryComics({
        category: isAll ? undefined : categoryName,
        region,
        status,
        tag,
        sort: sort as 'updated' | 'hot' | 'favorite',
      }),
    [categoryName, isAll, region, status, tag, sort],
  );

  const shown = result.slice(0, visible);

  const reset = () => {
    setRegion('全部');
    setStatus('全部');
    setTag('全部');
    setSort('updated');
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-7xl px-4 pt-6 md:px-6">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span>分类</span>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-800">{displayName}</span>
        </nav>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">
            {displayName}
          </h1>
          <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
            共 {result.length} 部作品
          </span>
        </div>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">{description}</p>
      </div>

      <div className="mx-auto mt-6 max-w-7xl px-4 md:px-6">
        <CategoryFilter
          region={region}
          status={status}
          tag={tag}
          sort={sort}
          onRegion={setRegion}
          onStatus={setStatus}
          onTag={setTag}
          onSort={setSort}
          onReset={reset}
        />
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
        {shown.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {shown.map((c) => (
                <ComicCard key={c.id} comic={c} showDescription />
              ))}
            </div>

            {visible < result.length ? (
              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 cursor-pointer"
                >
                  <i className="ri-add-line" />
                  加载更多（剩余 {result.length - visible} 部）
                </button>
              </div>
            ) : (
              <p className="mt-10 text-center text-sm text-foreground-400">已经到底啦，没有更多内容了</p>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-emotion-sad-line text-3xl" />
            </span>
            <h2 className="mt-4 text-lg font-semibold text-foreground-900">没有找到符合条件的漫画</h2>
            <p className="mt-1 text-sm text-foreground-500">试试更换筛选条件，或浏览全部作品</p>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={reset}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
              <Link
                to="/category/all"
                className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                浏览全部漫画
              </Link>
            </div>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}